import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { 
  Form, 
  Button, 
  Segment, 
  Header, 
  Message, 
  Dropdown,
  Input,
  TextArea,
  Checkbox,
  Dimmer,
  Loader
} from 'semantic-ui-react';

import {
  SG_FETCH_ACTION_TEMPLATE,
  SG_ADD_ACTION_TEMPLATE,
  SG_EDIT_ACTION_TEMPLATE,
  SG_GET_ORGANIZATION_SELECT
} from "constants/actions";

const EMAIL_TYPE_OPTIONS = [
  { key: 'report_delivery', value: 'report_delivery', text: 'Report Delivery' },
  { key: 'action_planning_invite', value: 'action_planning_invite', text: 'Action Planning Invite' },
  { key: 'check_in_reminder', value: 'check_in_reminder', text: 'Check-in Reminder' },
  { key: 'milestone_achieved', value: 'milestone_achieved', text: 'Milestone Achieved' },
  { key: 'at_risk_alert', value: 'at_risk_alert', text: 'At Risk Alert' },
  { key: 'progress_update', value: 'progress_update', text: 'Progress Update' }
];

const LANGUAGE_OPTIONS = [
  { key: 'en', value: 'en', text: 'English' },
  { key: 'es', value: 'es', text: 'Spanish' },
  { key: 'fr', value: 'fr', text: 'French' }
];

const EmailTemplateConfigAddEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    organization: '',
    email_type: '',
    language: 'en',
    postmark_template_id: '',
    postmark_template_alias: '',
    template_name: '',
    template_subject: '',
    custom_variables: {},
    default_content_overrides: {},
    is_active: true,
    is_verified: false,
    fallback_subject: '',
    fallback_content: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const { organizations, template } = useSelector((state) => ({
    organizations: state.organizations?.organization_select || [],
    template: id ? state.action_template?.[id] : null
  }));

  useEffect(() => {
    dispatch({
      type: SG_GET_ORGANIZATION_SELECT
    });
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && id) {
      setLoading(true);
      dispatch({
        type: SG_FETCH_ACTION_TEMPLATE,
        payload: { id }
      });
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && template) {
      setFormData({
        organization: template.organization || '',
        email_type: template.email_type || '',
        language: template.language || 'en',
        postmark_template_id: template.postmark_template_id || '',
        postmark_template_alias: template.postmark_template_alias || '',
        template_name: template.template_name || '',
        template_subject: template.template_subject || '',
        custom_variables: template.custom_variables || {},
        default_content_overrides: template.default_content_overrides || {},
        is_active: template.is_active !== undefined ? template.is_active : true,
        is_verified: template.is_verified || false,
        fallback_subject: template.fallback_subject || '',
        fallback_content: template.fallback_content || ''
      });
      setLoading(false);
    }
  }, [template, isEdit]);

  const handleInputChange = (e, { name, value, checked }) => {
    if (name === 'is_active' || name === 'is_verified') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email_type) {
      newErrors.email_type = 'Email type is required';
    }
    if (!formData.language) {
      newErrors.language = 'Language is required';
    }
    if (!formData.postmark_template_id && !formData.postmark_template_alias) {
      newErrors.template_identifier = 'Either Postmark Template ID or Alias is required';
    }
    if (formData.postmark_template_id && formData.postmark_template_alias) {
      newErrors.template_identifier = 'Provide either Template ID OR Alias, not both';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    const payload = {
      ...formData,
      postmark_template_id: formData.postmark_template_id ? parseInt(formData.postmark_template_id) : null,
    };

    if (isEdit) {
      dispatch({
        type: SG_EDIT_ACTION_TEMPLATE,
        payload: { id, data: payload }
      });
    } else {
      dispatch({
        type: SG_ADD_ACTION_TEMPLATE,
        payload: payload
      });
    }

    // Navigate back after save
    setTimeout(() => {
      setSaving(false);
      navigate('../');
    }, 1500);
  };

  const organizationOptions = organizations.map(org => ({
    key: org.id,
    value: org.id,
    text: org.name
  }));

  if (loading) {
    return (
      <Segment>
        <Dimmer active>
          <Loader>Loading email template configuration...</Loader>
        </Dimmer>
      </Segment>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <Button onClick={() => navigate('../')}>
          ← Back to Email Template Configurations
        </Button>
      </div>

      <Header as="h2">
        {isEdit ? 'Edit Email Template Configuration' : 'Add Email Template Configuration'}
      </Header>

      <Form onSubmit={handleSubmit}>
        <Segment>
          <Header as="h3">Basic Configuration</Header>
          
          <Form.Field error={!!errors.organization}>
            <label>Organization</label>
            <Dropdown
              placeholder="Select organization"
              fluid
              selection
              clearable
              options={organizationOptions}
              name="organization"
              value={formData.organization}
              onChange={handleInputChange}
            />
            {errors.organization && <Message color="red" size="small">{errors.organization}</Message>}
          </Form.Field>

          <Form.Field error={!!errors.email_type}>
            <label>Email Type</label>
            <Dropdown
              placeholder="Select email type"
              fluid
              selection
              options={EMAIL_TYPE_OPTIONS}
              name="email_type"
              value={formData.email_type}
              onChange={handleInputChange}
            />
            {errors.email_type && <Message color="red" size="small">{errors.email_type}</Message>}
          </Form.Field>

          <Form.Field error={!!errors.language}>
            <label>Language</label>
            <Dropdown
              placeholder="Select language"
              fluid
              selection
              options={LANGUAGE_OPTIONS}
              name="language"
              value={formData.language}
              onChange={handleInputChange}
            />
            {errors.language && <Message color="red" size="small">{errors.language}</Message>}
          </Form.Field>
        </Segment>

        <Segment>
          <Header as="h3">Postmark Template Configuration</Header>
          
          {errors.template_identifier && (
            <Message color="red" size="small">{errors.template_identifier}</Message>
          )}

          <Form.Field>
            <label>Postmark Template ID</label>
            <Input
              placeholder="Enter Postmark Template ID (numeric)"
              name="postmark_template_id"
              value={formData.postmark_template_id}
              onChange={handleInputChange}
              type="number"
            />
          </Form.Field>

          <Form.Field>
            <label>Postmark Template Alias</label>
            <Input
              placeholder="Enter Postmark Template Alias (preferred)"
              name="postmark_template_alias"
              value={formData.postmark_template_alias}
              onChange={handleInputChange}
            />
          </Form.Field>

          <Form.Field>
            <label>Template Name (cached)</label>
            <Input
              placeholder="Template name from Postmark"
              name="template_name"
              value={formData.template_name}
              onChange={handleInputChange}
            />
          </Form.Field>

          <Form.Field>
            <label>Template Subject (cached)</label>
            <Input
              placeholder="Template subject from Postmark"
              name="template_subject"
              value={formData.template_subject}
              onChange={handleInputChange}
            />
          </Form.Field>
        </Segment>

        <Segment>
          <Header as="h3">Fallback Configuration</Header>
          
          <Form.Field>
            <label>Fallback Subject</label>
            <Input
              placeholder="Subject to use if template fails"
              name="fallback_subject"
              value={formData.fallback_subject}
              onChange={handleInputChange}
            />
          </Form.Field>

          <Form.Field>
            <label>Fallback Content</label>
            <TextArea
              placeholder="Content to use if template fails"
              name="fallback_content"
              value={formData.fallback_content}
              onChange={handleInputChange}
              rows={4}
            />
          </Form.Field>
        </Segment>

        <Segment>
          <Header as="h3">Status</Header>
          
          <Form.Field>
            <Checkbox
              label="Is Active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
            />
          </Form.Field>

          <Form.Field>
            <Checkbox
              label="Is Verified (template exists in Postmark)"
              name="is_verified"
              checked={formData.is_verified}
              onChange={handleInputChange}
              disabled
            />
          </Form.Field>
        </Segment>

        <div style={{ marginTop: '1rem' }}>
          <Button 
            type="submit" 
            primary 
            loading={saving}
            disabled={saving}
          >
            {isEdit ? 'Update Configuration' : 'Create Configuration'}
          </Button>
          <Button 
            type="button" 
            onClick={() => navigate('../')}
            style={{ marginLeft: '0.5rem' }}
          >
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EmailTemplateConfigAddEdit;
